import { vrmConfig } from '@/config';
import {
    BATTERY_FLOW_RESTRICTIONS,
    MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL,
    DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL,
} from '@/vue/components/pages/dynamic-ess/constants';
import {
    absedFormula,
    fromAbsedString,
    removeImplicitMultiplication,
} from '@/vue/components/pages/dynamic-ess/utils';
import i18n from '@/vue/languages';

const namespace = 'dynamic-ess-form/';

export const idCountryUnitedKingdom = 36;
export const idProviderOctopus = 49;
export const ID_COUNTRY_SPAIN = 26; //Id of Spain as a country
export const ID_BIDDING_ZONE_SPAIN = 40; //Id of Spain as a bidding zone
export const ID_COUNTRY_FRANCE = 10; //Id of France as a country
export const ID_PROVIDER_EDF_TEMPO = 51; //Id of EDF Tempo as a provider
export const ID_PROVIDERS_VARIO = [66, 67, 68]; //Ids of Vario providers

export const PRICE_SAMPLING_RATES = [
    {
        value: 60,
        labelKey: 'dynamicess.priceSamplingRates.60',
        labelFallback: '60 Minutes',
    },
    {
        value: 15,
        labelKey: 'dynamicess.priceSamplingRates.15',
        labelFallback: '15 Minutes',
    },
];
export const DYNAMIC_ESS = Object.freeze({
    SET_FORM_FIELD: namespace + 'set_form_field',
    GRID_VALID: namespace + 'validate_grid',
    BATTERY_VALID: namespace + 'validate_battery',
    BUY_VALID: namespace + 'validate_buy',
    SELL_VALID: namespace + 'validate_sell',
    FORM_VALID: namespace + 'validate_form',
    FORM: namespace + 'form',
    SUBMIT_FORM: namespace + 'submit_form',
    TO_API_DTO: namespace + 'to_api_dto',
    SET_FORM_FROM_API: namespace + 'set_form_from_api',
    FILL_ZONES: namespace + 'fill_zones',
    BIDDING_ZONES: namespace + 'bidding_zones',
    PROVIDERS: namespace + 'providers',
    FILL_PROVIDERS: namespace + 'fill_providers',
    SET_UP_COUNTRY_BASED_ON_BIDDING: namespace + 'set_up_country_based_on_bidding',
    SET_UP_PROVIDERS_MAPPING: namespace + 'set_up_providers',
    CAPABILITIES: namespace + 'capabilities',
    SET_CAPABILITIES: namespace + 'set_capabilities',
    BATTERY_FLOW_RESTRICTION_TYPE: namespace + 'battery_flow_restriction_type',
    BATTERY_FLOW_RESTRICTION_SUPPORTED: namespace + 'battery_flow_restriction_supported',
});

// poor man's typescript
export const DYNAMIC_ESS_FORM_FIELDS = {
    // BUY/SELL step
    country: 'country',
    idBiddingZone: 'idBiddingZone',
    biddingZone: 'biddingZone',
    // GRID step
    energyGridSell: 'energyGridSell', // BOOLEAN
    maximumImportPower: 'maximumImportPower', // NUMBER
    maximumExportPower: 'maximumExportPower', // NUMBER
    isGreenModeOn: 'isGreenModeOn', // BOOLEAN
    // BATTERY step
    batteryEnergyCapacity: 'batteryEnergyCapacity', // NUMBER
    maximumDischargePower: 'maximumDischargePower', // NUMBER
    totalEstimatedBatteryCycles: 'totalEstimatedBatteryCycles', // NUMBER
    batteryPrice: 'batteryPrice', // NUMBER
    maximumChargePower: 'maximumChargePower', // NUMBER
    batteryCosts: 'batteryCosts', // NUMBER
    dessSocDifferent: 'dessSocDifferent', // BOOLEAN
    isPeriodicFullChargeOn: 'isPeriodicFullChargeOn', // BOOLEAN
    periodicFullChargeInterval: 'periodicFullChargeInterval', // NUMBER
    batteryFlowRestriction: 'batteryFlowRestriction', // BATTERY_FLOW_RESTRICTIONS
    alwaysApplyBatteryFlowRestriction: 'alwaysApplyBatteryFlowRestriction', // BOOLEAN
    batteryFlowRestrictionDifferentOnWeekends: 'batteryFlowRestrictionDifferentOnWeekends', // BOOLEAN,
    batteryFlowRestrictionRowsWeek: 'batteryFlowRestrictionRowsWeek', // ARRAY
    batteryFlowRestrictionRowsWeekend: 'batteryFlowRestrictionRowsWeekend', // ARRAY
    batteryFlowRestrictionErrorRowsWeek: 'batteryFlowRestrictionErrorRowsWeek',
    batteryFlowRestrictionErrorRowsWeekend: 'batteryFlowRestrictionErrorRowsWeekend',
    // BUY step
    fixedBuyPrices: 'fixedBuyPrices', // BOOLEAN,
    buyDifferentPricesWeek: 'buyDifferentPricesWeek', // BOOLEAN
    buyRowsWeek: 'buyRowsWeek', // ARRAY
    buyRowsWeekend: 'buyRowsWeekend', // ARRAY
    buyPriceFormula: 'buyPriceFormula', // STRING
    buyErrorRowsWeek: 'buyErrorRowsWeek',
    buyErrorRowsWeekend: 'buyErrorRowsWeekend',
    buyProvider: 'buyProvider', // object chosen from dropdown
    buyProviderId: 'buyProviderId', // filled from api
    buyProviderName: 'buyProviderName',
    buyPriceSamplingRate: 'buyPriceSamplingRate',
    buyErrorPriceFormula: 'buyErrorPriceFormula',
    accountNumber: 'accountNumber', //Octopus account number
    apiKey: 'apiKey', //Octopus API key
    directDebit: 'directDebit',
    tempoOverwriteBlueDayHours: 'tempoOverwriteBlueDayHours', //EDF - Tempo Blue day hours
    tempoOverwriteBlueNightHours: 'tempoOverwriteBlueNightHours', //EDF - Tempo Blue night hours
    tempoOverwriteRedDayHours: 'tempoOverwriteRedDayHours', //EDF - Tempo Red day hours
    tempoOverwriteRedNightHours: 'tempoOverwriteRedNightHours', //EDF - Tempo Red night hours
    tempoOverwriteWhiteDayHours: 'tempoOverwriteWhiteDayHours', //EDF - Tempo White day hours
    tempoOverwriteWhiteNightHours: 'tempoOverwriteWhiteNightHours', //EDF - Tempo White night hours
    vat: 'vat',
    buyPriceClampNegativeToZero: 'buyPriceClampNegativeToZero',
    // SELL step
    fixedSellPrices: 'fixedSellPrices', // BOOLEAN,
    sellDifferentPricesWeek: 'sellDifferentPricesWeek', // BOOLEAN
    sellRowsWeek: 'sellRowsWeek', // ARRAY
    sellRowsWeekend: 'sellRowsWeekend', // ARRAY
    sellPriceFormula: 'sellPriceFormula', // STRING
    sellErrorRowsWeek: 'sellErrorRowsWeek',
    sellErrorRowsWeekend: 'sellErrorRowsWeekend',
    sellProvider: 'sellProvider',
    sellProviderId: 'sellProviderId', // filled from api
    sellProviderName: 'sellProviderName',
    sellPriceSamplingRate: 'sellPriceSamplingRate',
    sellErrorPriceFormula: 'sellErrorPriceFormula',
    sellPriceClampNegativeToZero: 'sellPriceClampNegativeToZero',
};

export const DESS_CAPABILITIES = {
    batteryFlowRestrictionsSupported: 1, // bitmask representation
};

const validateInputNr = (value, { allowNegative = false, allowZero = true } = {}) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (!allowNegative && +value < 0) {
        return false;
    }

    if (!allowZero && +value === 0) {
        return false;
    }

    return !isNaN(value);
};

const WEEK = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];
const convertFromApiSchedule = (schedule, valueField = 'price') => {
    return schedule.map(s => ({
        from: { time: s.from, error: false },
        to: { time: s.to, error: false },
        [valueField]: { value: s[valueField], error: false },
    }));
};
const convertToApiSchedule = (differentValuesWeek, rowsWeek, rowsWeekend, valueField = 'price') => {
    if (rowsWeek.length === 0 && rowsWeekend.length === 0) {
        return null;
    }
    const schedule = [];
    const mapRow = rows =>
        rows.map(r => {
            let value = r[valueField].value;
            // Keep booleans as they are
            if (typeof value !== 'boolean') {
                value = +value;
            }

            return {
                from: r.from.time,
                to: r.to.time,
                [valueField]: value,
            };
        });
    if (differentValuesWeek) {
        const weekSchedule = mapRow(rowsWeek);
        schedule.push({
            days: WEEK,
            schedule: weekSchedule,
        });

        const weekendSchedule = mapRow(rowsWeekend);
        schedule.push({
            days: WEEKEND,
            schedule: weekendSchedule,
        });
    } else {
        schedule.push({
            days: [...WEEK, ...WEEKEND],
            schedule: mapRow(rowsWeek),
        });
    }
    // return schedule;
    return schedule;
};

export const countriesTranslations = country => `dynamicess.countries.${country}`;

const getTableForPrices = ({
    type,
    fixed,
    rowsWeek,
    rowsWeekend,
    priceFormula,
    differentPricesWeek,
    providerCountry,
    providerZone,
    providerName,
    priceSamplingRateValue,
    clampNegativePricesToZero,
    tempoOverwrite = [],
}) => {
    if (!fixed) {
        const shouldNotShowPriceCalculation = providerName => {
            return (
                providerName === 'Octopus' ||
                providerName === 'RE-ES' ||
                providerName === 'EDF - Tempo' ||
                (typeof providerName === 'string' && providerName.includes('Vario'))
            );
        };

        const zone =
            providerZone === providerCountry
                ? [] // not showing zone
                : [
                      {
                          label: i18n.t(`dynamicess.energyProviderBiddingZone`, {
                              i18nFallback: 'Energy provider bidding zone',
                          }),
                          value: providerZone,
                      },
                  ];
        const edfTempoValues =
            tempoOverwrite && providerName === 'EDF - Tempo'
                ? tempoOverwrite.map(item => {
                      return {
                          label: `EDF - Tempo ${item.name}`,
                          value: item.value ? `€ ${item.value}` : null,
                      };
                  })
                : [];

        const priceCalculation = shouldNotShowPriceCalculation(providerName)
            ? [] // not showing formula
            : [
                  {
                      label: i18n.t('dynamicess.priceCalculation', {
                          i18nFallback: 'Price calculation',
                      }),
                      value: absedFormula(priceFormula),
                  },
              ];

        const showNegativePrices =
            providerName === 'EDF - Tempo' ||
            (typeof providerName === 'string' && providerName.includes('Vario'))
                ? []
                : [
                      {
                          label: i18n.t('dynamicess.toggleNegativePriceClamping', {
                              i18nFallback:
                                  'Does your energy provider support negative prices? Otherwise negative prices will be considered the same as 0.',
                          }),
                          value: !clampNegativePricesToZero
                              ? i18n.t('dynamicess.choice_1', { i18nFallback: 'Yes' })
                              : i18n.t('dynamicess.choice_0', { i18nFallback: 'No' }),
                      },
                  ];

        const priceSamplingRate = PRICE_SAMPLING_RATES.find(
            option => option.value === priceSamplingRateValue,
        );
        const priceSampling =
            priceSamplingRate === undefined
                ? []
                : [
                      {
                          label: i18n.t(`dynamicess.priceSamplingRate`, {
                              i18nFallback: 'Price sampling rate',
                          }),
                          value: i18n.t(priceSamplingRate.labelKey, {
                              i18nFallback: priceSamplingRate.labelFallback,
                          }),
                      },
                  ];
        return [
            {
                label: i18n.t(`dynamicess.${type}PricesType`, {
                    i18nFallback: type === 'buy' ? 'Buy prices type' : 'Sell prices type',
                }),
                value: i18n.t('dynamicess.buy_type_0', { i18nFallback: 'Dynamic' }),
            },
            {
                label: i18n.t(`dynamicess.energyProviderCountry`, {
                    i18nFallback: 'Energy provider country',
                }),
                value: i18n.t(countriesTranslations(providerCountry), {
                    i18nFallback: providerCountry,
                }),
            },
            ...zone,
            {
                label: i18n.t(`dynamicess.energyProviderName`, {
                    i18nFallback: 'Energy provider name',
                }),
                value: providerName,
            },
            ...priceSampling,
            ...edfTempoValues,
            ...priceCalculation,
            ...showNegativePrices,
        ];
    }
    return [
        {
            label: i18n.t(`dynamicess.${type}PricesType`, {
                i18nFallback: type === 'buy' ? 'Buy prices type' : 'Sell prices type',
            }),
            value: i18n.t('dynamicess.buy_type_1', { i18nFallback: 'Fixed' }),
        },
        {
            label: i18n.t('dynamicess.differentPricesWeekdays', {
                i18nFallback: 'Different prices for weekdays and weekends?',
            }),
            value: differentPricesWeek
                ? i18n.t('dynamicess.choice_1', { i18nFallback: 'Yes' })
                : i18n.t('dynamicess.choice_0', { i18nFallback: 'No' }),
        },
        ...getFixedScheduleTables(rowsWeek, rowsWeekend, differentPricesWeek),
    ].filter(Boolean);
};

function getFixedScheduleTables(rowsWeek, rowsWeekend, differentPricesWeek) {
    return [
        {
            label: i18n.t('dynamicess.scheduleWeekdays', { i18nFallback: 'Weekdays schedule' }),
        },
        { table: true, value: rowsWeek },
        differentPricesWeek && {
            label: i18n.t('dynamicess.scheduleWeekend', { i18nFallback: 'Weekend schedule' }),
        },
        differentPricesWeek && {
            table: true,
            value: rowsWeekend,
        },
    ].filter(Boolean);
}

function formatNumberDecimals(number, keepIntegerAsInteger) {
    if (keepIntegerAsInteger && Number.isInteger(number)) {
        return number;
    }
    return parseFloat(number).toFixed(2);
}

export function createForm() {
    const state = Object.keys(DYNAMIC_ESS_FORM_FIELDS).reduce((acc, key) => {
        const typeArrays = [
            'buyRowsWeek',
            'buyRowsWeekend',
            'sellRowsWeek',
            'sellRowsWeekend',
            'batteryFlowRestrictionRowsWeek',
            'batteryFlowRestrictionRowsWeekend',
        ];
        if (typeArrays.includes(key)) {
            acc[key] = [];
            return acc;
        }

        acc[key] = null;
        return acc;
    }, {});
    state.providersByCountry = {};
    state.providers = [];
    state.countries = [];
    state.biddingZonesByCountry = {};
    state.biddingZones = [];
    state.sellPriceFormula = 'p';
    state.buyPriceFormula = 'p';
    state.capabilities = [];
    return state;
}

export function applyData(state, data) {
    const {
        gridSell = null,
        buyPriceType = null,
        sellPriceType = null,
        batteryCapacity = null,
        dischargePower = null,
        chargePower = null,
        batteryCosts = null,
        maxPowerFromGrid = null,
        totalEstimatedBatteryCycles = null,
        isPeriodicFullChargeOn = null,
        periodicFullChargeInterval = null,
        batteryPrice = null,
        maxPowerToGrid = null,
        buyPriceFormula = null,
        sellPriceFormula = null,
        buyEnergyProviderName = null,
        sellEnergyProviderName = null,
        idBiddingZone = null,
        buyPriceSamplingRate = null,
        sellPriceSamplingRate = null,
        buyEnergyProviderId = null,
        sellEnergyProviderId = null,
        isDessSocDifferent = null,
        batteryFlowRestriction = null,
        batteryChargeRestrictionSchedule,
        batteryDischargeRestrictionSchedule,
        alwaysApplyBatteryFlowRestriction,
        isGreenModeOn = null,
        buyPriceClampNegativeToZero = null,
        sellPriceClampNegativeToZero = null,
        apiKey = null,
        accountName = null,
        directDebit = null,
        vat = null,
        tempoOverwriteBlueDayHours = null,
        tempoOverwriteBlueNightHours = null,
        tempoOverwriteRedDayHours = null,
        tempoOverwriteRedNightHours = null,
        tempoOverwriteWhiteDayHours = null,
        tempoOverwriteWhiteNightHours = null,
    } = data;
    const buyPriceSchedule = JSON.parse(data.buyPriceSchedule || 'null');
    const sellPriceSchedule = JSON.parse(data.sellPriceSchedule || 'null');
    // todo: renaming/mapping will clean up this mess
    state.batteryEnergyCapacity = batteryCapacity;
    state.maximumDischargePower = dischargePower;
    state.totalEstimatedBatteryCycles = totalEstimatedBatteryCycles;
    state.batteryPrice = batteryPrice;
    state.maximumChargePower = chargePower;
    state.batteryCosts = batteryCosts;
    state.energyGridSell = gridSell;
    state.maximumImportPower = maxPowerFromGrid;
    state.maximumExportPower = maxPowerToGrid;
    state.sellPriceFormula = removeImplicitMultiplication(fromAbsedString(sellPriceFormula));
    state.buyPriceFormula = removeImplicitMultiplication(fromAbsedString(buyPriceFormula));
    state.fixedBuyPrices = buyPriceType !== null ? +!buyPriceType : null;
    state.fixedSellPrices = sellPriceType !== null ? +!sellPriceType : null;
    state.buyPriceSamplingRate = buyPriceSamplingRate;
    state.sellPriceSamplingRate = sellPriceSamplingRate || buyPriceSamplingRate;
    state.isGreenModeOn = isGreenModeOn;
    state.buyPriceClampNegativeToZero = !!buyPriceClampNegativeToZero;
    state.sellPriceClampNegativeToZero = !!sellPriceClampNegativeToZero;

    // Buy/sell prices schedule
    state.buyDifferentPricesWeek =
        buyPriceSchedule !== null ? +(buyPriceSchedule && buyPriceSchedule.length === 2) : null;
    state.sellDifferentPricesWeek =
        sellPriceSchedule !== null ? +(sellPriceSchedule && sellPriceSchedule.length === 2) : null;
    state.buyRowsWeek = buyPriceSchedule
        ? convertFromApiSchedule(buyPriceSchedule[0].schedule)
        : [];
    state.buyRowsWeekend = state.buyDifferentPricesWeek
        ? convertFromApiSchedule(buyPriceSchedule[1].schedule)
        : [];
    state.sellRowsWeek = sellPriceSchedule
        ? convertFromApiSchedule(sellPriceSchedule[0].schedule)
        : [];
    state.sellRowsWeekend = state.sellDifferentPricesWeek
        ? convertFromApiSchedule(sellPriceSchedule[1].schedule)
        : [];

    // Battery flow restriction schedule
    let restrictionSchedule;
    if (batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.NO_EXPORT) {
        restrictionSchedule = JSON.parse(batteryDischargeRestrictionSchedule || '[]');
    } else if (batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.NO_IMPORT) {
        restrictionSchedule = JSON.parse(batteryChargeRestrictionSchedule || '[]');
    } else {
        restrictionSchedule = [];
    }
    state.batteryFlowRestrictionRowsWeek =
        restrictionSchedule.length > 0
            ? convertFromApiSchedule(restrictionSchedule[0].schedule, 'restrict')
            : [];
    state.batteryFlowRestrictionDifferentOnWeekends = restrictionSchedule.length === 2;
    state.batteryFlowRestrictionRowsWeekend = state.batteryFlowRestrictionDifferentOnWeekends
        ? convertFromApiSchedule(restrictionSchedule[1].schedule, 'restrict')
        : [];

    state.batteryFlowRestriction = batteryFlowRestriction;
    state.alwaysApplyBatteryFlowRestriction = !!alwaysApplyBatteryFlowRestriction;
    state.buyProviderName = buyEnergyProviderName;
    state.sellProviderName = sellEnergyProviderName;
    state.buyProviderId = buyEnergyProviderId;
    state.sellProviderId = sellEnergyProviderId;
    state.idBiddingZone = idBiddingZone;
    // todo: set 0 to null when it's brought back
    state.dessSocDifferent = isDessSocDifferent === null ? 0 : +data.isDessSocDifferent;
    state.isPeriodicFullChargeOn = isPeriodicFullChargeOn;
    // in case interval has never been set, set it to the default
    state.periodicFullChargeInterval = Math.max(
        periodicFullChargeInterval
            ? +periodicFullChargeInterval
            : DEFAULT_PERIODIC_FULL_CHARGE_INTERVAL,
        MINIMUM_PERIODIC_FULL_CHARGE_INTERVAL,
    );

    // UK Octopus specific fields.
    state.accountNumber = accountName;
    state.apiKey = apiKey;
    state.directDebit = !!directDebit;
    state.vat = !!vat;

    //France EDF Tempo specific fields
    state.tempoOverwriteBlueDayHours = tempoOverwriteBlueDayHours;
    state.tempoOverwriteBlueNightHours = tempoOverwriteBlueNightHours;
    state.tempoOverwriteRedDayHours = tempoOverwriteRedDayHours;
    state.tempoOverwriteRedNightHours = tempoOverwriteRedNightHours;
    state.tempoOverwriteWhiteDayHours = tempoOverwriteWhiteDayHours;
    state.tempoOverwriteWhiteNightHours = tempoOverwriteWhiteNightHours;
}

export default {
    state: createForm,

    getters: {
        [DYNAMIC_ESS.TO_API_DTO]: (state, getters) => {
            const {
                energyGridSell,
                fixedBuyPrices,
                fixedSellPrices,
                buyDifferentPricesWeek,
                sellDifferentPricesWeek,
                buyPriceSamplingRate,
                sellPriceSamplingRate,
                batteryEnergyCapacity,
                maximumDischargePower,
                maximumChargePower,
                totalEstimatedBatteryCycles,
                batteryPrice,
                batteryCosts,
                maximumImportPower,
                maximumExportPower,
                buyPriceFormula,
                sellPriceFormula,
                biddingZone,
                buyProvider,
                sellProvider,
                buyProviderName,
                sellProviderName,
                dessSocDifferent,
                isPeriodicFullChargeOn,
                periodicFullChargeInterval,
                batteryFlowRestriction,
                batteryFlowRestrictionRowsWeek,
                batteryFlowRestrictionRowsWeekend,
                batteryFlowRestrictionDifferentOnWeekends,
                alwaysApplyBatteryFlowRestriction,
                isGreenModeOn,
                buyPriceClampNegativeToZero,
                sellPriceClampNegativeToZero,
                apiKey,
                accountNumber,
                directDebit,
                tempoOverwriteBlueDayHours,
                tempoOverwriteBlueNightHours,
                tempoOverwriteRedDayHours,
                tempoOverwriteRedNightHours,
                tempoOverwriteWhiteDayHours,
                tempoOverwriteWhiteNightHours,
                vat,
            } = state;
            const data = {
                gridSell: +energyGridSell,
                buyPriceType: +!fixedBuyPrices,
                sellPriceType: +!fixedSellPrices,
                buyPriceSchedule: convertToApiSchedule(
                    buyDifferentPricesWeek,
                    state.buyRowsWeek,
                    state.buyRowsWeekend,
                ),
                sellPriceSchedule: convertToApiSchedule(
                    sellDifferentPricesWeek,
                    state.sellRowsWeek,
                    state.sellRowsWeekend,
                ),
                buyPriceSamplingRate: buyPriceSamplingRate,
                sellPriceSamplingRate: sellPriceSamplingRate,
                batteryCapacity: +batteryEnergyCapacity,
                dischargePower: +maximumDischargePower,
                chargePower: +maximumChargePower,
                batteryCosts: +batteryCosts,
                maxPowerFromGrid: +maximumImportPower,
                totalEstimatedBatteryCycles: +totalEstimatedBatteryCycles,
                batteryPrice: +batteryPrice,
                maxPowerToGrid: +maximumExportPower,
                buyPriceFormula: removeImplicitMultiplication(absedFormula(buyPriceFormula)),
                sellPriceFormula: removeImplicitMultiplication(absedFormula(sellPriceFormula)),
                isDessSocDifferent: !!dessSocDifferent,
                isPeriodicFullChargeOn: !!isPeriodicFullChargeOn,
                periodicFullChargeInterval: isPeriodicFullChargeOn
                    ? +periodicFullChargeInterval
                    : null,
                batteryFlowRestriction: batteryFlowRestriction,
                alwaysApplyBatteryFlowRestriction: +alwaysApplyBatteryFlowRestriction,
                isGreenModeOn: isGreenModeOn,
                buyPriceClampNegativeToZero: !!buyPriceClampNegativeToZero,
                sellPriceClampNegativeToZero: !!sellPriceClampNegativeToZero,
                accountName: accountNumber,
                directDebit: directDebit,
                vat: vat,
                apiKey: apiKey,
                tempoOverwriteBlueDayHours: +tempoOverwriteBlueDayHours,
                tempoOverwriteBlueNightHours: +tempoOverwriteBlueNightHours,
                tempoOverwriteRedDayHours: +tempoOverwriteRedDayHours,
                tempoOverwriteRedNightHours: +tempoOverwriteRedNightHours,
                tempoOverwriteWhiteDayHours: +tempoOverwriteWhiteDayHours,
                tempoOverwriteWhiteNightHours: +tempoOverwriteWhiteNightHours,
            };

            if (!getters[DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_SUPPORTED]) {
                data.batteryFlowRestriction = BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED;
            } else if (batteryFlowRestriction !== BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED) {
                const batteryFlowRestrictionScheduleKey =
                    batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.NO_EXPORT
                        ? 'batteryDischargeRestrictionSchedule'
                        : 'batteryChargeRestrictionSchedule';
                data[batteryFlowRestrictionScheduleKey] = convertToApiSchedule(
                    batteryFlowRestrictionDifferentOnWeekends,
                    batteryFlowRestrictionRowsWeek,
                    batteryFlowRestrictionRowsWeekend,
                    'restrict',
                );
            }

            if (isNaN(+totalEstimatedBatteryCycles) || +totalEstimatedBatteryCycles <= 0) {
                delete data.totalEstimatedBatteryCycles;
            }

            if (biddingZone) {
                data.idBiddingZone = biddingZone.idZone;
            }

            if (buyProvider && buyProvider.id !== -1) {
                data.buyEnergyProviderId = +buyProvider.idProvider;
            } else {
                data.buyEnergyProviderName = buyProviderName;
            }

            if (sellProvider && sellProvider.id !== -1) {
                data.sellEnergyProviderId = +sellProvider.idProvider;
            } else {
                data.sellEnergyProviderName = sellProviderName;
            }

            if (isNaN(+tempoOverwriteBlueDayHours) || +tempoOverwriteBlueDayHours <= 0) {
                data.tempoOverwriteBlueDayHours = null;
            }

            if (isNaN(+tempoOverwriteBlueNightHours) || +tempoOverwriteBlueNightHours <= 0) {
                data.tempoOverwriteBlueNightHours = null;
            }

            if (isNaN(+tempoOverwriteRedDayHours) || +tempoOverwriteRedDayHours <= 0) {
                data.tempoOverwriteRedDayHours = null;
            }

            if (isNaN(+tempoOverwriteRedNightHours) || +tempoOverwriteRedNightHours <= 0) {
                data.tempoOverwriteRedNightHours = null;
            }

            if (isNaN(+tempoOverwriteWhiteDayHours) || +tempoOverwriteWhiteDayHours <= 0) {
                data.tempoOverwriteWhiteDayHours = null;
            }

            if (isNaN(+tempoOverwriteWhiteNightHours) || +tempoOverwriteWhiteNightHours <= 0) {
                data.tempoOverwriteWhiteNightHours = null;
            }

            // can't post data if it's not on
            data.isOn = true;

            return data;
        },
        [DYNAMIC_ESS.FORM]: (state, getters) => currency => {
            const grid = [
                {
                    label: i18n.t('ess.energy_sell_question', {
                        i18nFallback: 'Can you sell energy back to the grid??',
                    }),
                    value: state.energyGridSell
                        ? i18n.t('dynamicess.choice_1', { i18nFallback: 'Yes' })
                        : i18n.t('dynamicess.choice_0', { i18nFallback: 'No' }),
                },
                {
                    label: i18n.t('dynamicess.maxImport', { i18nFallback: 'Maximum import power' }),
                    value: `${state.maximumImportPower} ${i18n.t('ess.kw', {
                        i18nFallback: 'kW',
                    })}`,
                },
                state.energyGridSell && {
                    label: i18n.t('dynamicess.maxExport', { i18nFallback: 'Maximum export power' }),
                    value: `${state.maximumExportPower} ${i18n.t('ess.kw', {
                        i18nFallback: 'kW',
                    })}`,
                },
                {
                    label: i18n.t('dynamicess.operatingMode', { i18nFallback: 'Operating mode' }),
                    value: state.isGreenModeOn
                        ? i18n.t('dynamicess.modeGreen', { i18nFallback: 'Green mode' })
                        : i18n.t('dynamicess.modeTrade', { i18nFallback: 'Trade mode' }),
                },
            ].filter(Boolean);

            const batteryFlowRestrictionSupported =
                getters[DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_SUPPORTED];
            const applyBatteryFlowRestrictions =
                batteryFlowRestrictionSupported &&
                state.batteryFlowRestriction !== null &&
                state.batteryFlowRestriction !== BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED;
            const hasFixedBatteryFlowRestrictionSchedule =
                applyBatteryFlowRestrictions && state.alwaysApplyBatteryFlowRestriction === false;
            let battery = [
                {
                    label: i18n.t('dynamicess.batteryCapacity', {
                        i18nFallback: 'Maximum amount of energy that can be stored in the battery',
                    }),
                    value: `${state.batteryEnergyCapacity} ${i18n.t('dynamicess.kwh', {
                        i18nFallback: 'kWh',
                    })}`,
                },
                {
                    label: i18n.t('ess.max_battery_discharge_power', {
                        i18nFallback: 'Maximum discharge power',
                    }),
                    value: `${state.maximumDischargePower} ${i18n.t('ess.kw', {
                        i18nFallback: 'kW',
                    })}`,
                },
                {
                    label: i18n.t('ess.max_charge_power', { i18nFallback: 'Maximum charge power' }),
                    value: `${state.maximumChargePower} ${i18n.t('ess.kw', {
                        i18nFallback: 'kW',
                    })}`,
                },
                {
                    label: i18n.t('dynamicess.totalEstimatedBatteryCycles', {
                        i18nFallback: 'Total estimated battery cycles',
                    }),
                    value: state.totalEstimatedBatteryCycles
                        ? state.totalEstimatedBatteryCycles
                        : '-',
                },
                {
                    label: i18n.t('dynamicess.batteryPrice', { i18nFallback: 'Battery price' }),
                    value: state.batteryPrice
                        ? `${currency.sign} ${formatNumberDecimals(state.batteryPrice, true)}`
                        : '-',
                },
                {
                    label: i18n.t('ess.battery_costs', {
                        i18nFallback: 'Battery cycle costs per kWh',
                    }),
                    value: state.batteryCosts
                        ? `${formatNumberDecimals(state.batteryCosts, false)}
                        ${currency.sign}/${i18n.t('dynamicess.kwh', {
                              i18nFallback: 'kWh',
                          })}`
                        : '-',
                },
                {
                    label: i18n.t('dynamicess.batteryBalancing', {
                        i18nFallback: 'Battery balancing',
                    }),
                    value: state.isPeriodicFullChargeOn
                        ? i18n.t('common.on', {
                              i18nFallback: 'On',
                          })
                        : i18n.t('common.off', {
                              i18nFallback: 'Off',
                          }),
                },
                state.isPeriodicFullChargeOn && {
                    label: i18n.t('dynamicess.fullChargeInterval', {
                        i18nFallback: 'Full charge interval',
                    }),
                    value: `${state.periodicFullChargeInterval} ${
                        state.periodicFullChargeInterval > 1
                            ? i18n.t('dynamicess.days', {
                                  i18nFallback: 'days',
                              })
                            : i18n.t('dynamicess.day', {
                                  i18nFallback: 'day',
                              })
                    }`,
                },
                batteryFlowRestrictionSupported && {
                    label: i18n.t('dynamicess.batteryFlowRestrictionQuestion', {
                        i18nFallback: 'Do you need to disable grid charging or discharging?',
                    }),
                    value: i18n.t(`dynamicess.batteryFlowOptions.${state.batteryFlowRestriction}`, {
                        i18nFallback: `${state.batteryFlowRestriction}`,
                    }),
                },
                applyBatteryFlowRestrictions && {
                    label: i18n.t('dynamicess.whenBatteryFlowRestrictionsApply', {
                        i18nFallback: 'When do the restrictions apply?',
                    }),
                    value: state.alwaysApplyBatteryFlowRestriction
                        ? i18n.t('dynamicess.applyBatteryFlowRestrictionOptions.always', {
                              i18nFallback: 'Always',
                          })
                        : i18n.t('dynamicess.applyBatteryFlowRestrictionOptions.atSpecificTimes', {
                              i18nFallback: 'At specific times',
                          }),
                },
            ].filter(Boolean);

            if (hasFixedBatteryFlowRestrictionSchedule) {
                battery = [
                    ...battery,
                    ...getFixedScheduleTables(
                        state.batteryFlowRestrictionRowsWeek,
                        state.batteryFlowRestrictionRowsWeekend,
                        state.batteryFlowRestrictionDifferentOnWeekends,
                    ),
                ];
            }

            const buy = getTableForPrices({
                type: 'buy',
                fixed: state.fixedBuyPrices,
                rowsWeek: state.buyRowsWeek,
                rowsWeekend: state.buyRowsWeekend,
                priceFormula: state.buyPriceFormula,
                differentPricesWeek: state.buyDifferentPricesWeek,
                providerCountry: state.country && state.country.name,
                providerZone: state.biddingZone && state.biddingZone.name,
                providerName: state.buyProviderName,
                priceSamplingRateValue: state.buyPriceSamplingRate,
                clampNegativePricesToZero: state.buyPriceClampNegativeToZero,
                tempoOverwrite: [
                    {
                        name: i18n.t('dynamicess.edfTempo.blueDayTitle', {
                            i18nFallback: 'Blue day hours price',
                        }),
                        value: state.tempoOverwriteBlueDayHours,
                    },
                    {
                        name: i18n.t('dynamicess.edfTempo.blueNightTitle', {
                            i18nFallback: 'Blue night hours price',
                        }),
                        value: state.tempoOverwriteBlueNightHours,
                    },
                    {
                        name: i18n.t('dynamicess.edfTempo.whiteDayTitle', {
                            i18nFallback: 'White day hours price',
                        }),
                        value: state.tempoOverwriteWhiteDayHours,
                    },
                    {
                        name: i18n.t('dynamicess.edfTempo.whiteNightTitle', {
                            i18nFallback: 'White night hours price',
                        }),
                        value: state.tempoOverwriteWhiteNightHours,
                    },
                    {
                        name: i18n.t('dynamicess.edfTempo.redDayTitle', {
                            i18nFallback: 'Red day hours price',
                        }),
                        value: state.tempoOverwriteRedDayHours,
                    },
                    {
                        name: i18n.t('dynamicess.edfTempo.redNightTitle', {
                            i18nFallback: 'Red night hours price',
                        }),
                        value: state.tempoOverwriteRedNightHours,
                    },
                ],
            });
            const sell = getTableForPrices({
                type: 'sell',
                fixed: state.fixedSellPrices,
                rowsWeek: state.sellRowsWeek,
                rowsWeekend: state.sellRowsWeekend,
                priceFormula: state.sellPriceFormula,
                differentPricesWeek: state.sellDifferentPricesWeek,
                providerCountry: state.country && state.country.name,
                providerZone: state.biddingZone && state.biddingZone.name,
                providerName: state.sellProviderName,
                priceSamplingRateValue: state.sellPriceSamplingRate,
                clampNegativePricesToZero: state.sellPriceClampNegativeToZero,
            });
            return { grid, battery, buy, sell };
        },
        // FORM VALIDATION
        [DYNAMIC_ESS.GRID_VALID]: state => {
            return (
                state.energyGridSell !== null &&
                validateInputNr(state.maximumImportPower) &&
                (state.energyGridSell ? validateInputNr(state.maximumExportPower) : true) &&
                state.isGreenModeOn !== null
            );
        },
        [DYNAMIC_ESS.BATTERY_VALID]: (state, getters) => currencyCode => {
            const costInEuroUnder10 = currencyCode === 'EUR' ? +state.batteryCosts < 10 : true;
            const {
                totalEstimatedBatteryCycles,
                batteryFlowRestriction,
                batteryFlowRestrictionRowsWeek,
                batteryFlowRestrictionRowsWeekend,
                batteryFlowRestrictionErrorRowsWeek,
                batteryFlowRestrictionErrorRowsWeekend,
                batteryFlowRestrictionDifferentOnWeekends,
                alwaysApplyBatteryFlowRestriction,
            } = state;
            const validTotalEstimatedBatteryCycles =
                totalEstimatedBatteryCycles !== null &&
                totalEstimatedBatteryCycles !== '' &&
                totalEstimatedBatteryCycles !== undefined;

            const fixedValid =
                batteryFlowRestriction !== null &&
                (alwaysApplyBatteryFlowRestriction ||
                    batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.UNRESTRICTED ||
                    (!batteryFlowRestrictionDifferentOnWeekends &&
                        batteryFlowRestrictionRowsWeek.length > 0 &&
                        !batteryFlowRestrictionErrorRowsWeek) ||
                    (batteryFlowRestrictionDifferentOnWeekends &&
                        batteryFlowRestrictionRowsWeek.length > 0 &&
                        batteryFlowRestrictionRowsWeekend.length > 0 &&
                        !batteryFlowRestrictionErrorRowsWeek &&
                        !batteryFlowRestrictionErrorRowsWeekend));
            const restrictionsValid = getters[DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_SUPPORTED]
                ? fixedValid
                : true;
            return (
                validateInputNr(state.maximumDischargePower, {
                    allowNegative: false,
                    allowZero: false,
                }) &&
                validateInputNr(state.maximumChargePower, {
                    allowNegative: false,
                    allowZero: false,
                }) &&
                validateInputNr(state.batteryEnergyCapacity) &&
                state.batteryEnergyCapacity > 0 &&
                validateInputNr(state.batteryCosts) &&
                (validTotalEstimatedBatteryCycles ? +totalEstimatedBatteryCycles > 0 : true) &&
                costInEuroUnder10 &&
                state.isPeriodicFullChargeOn != null &&
                (state.isPeriodicFullChargeOn ? +state.periodicFullChargeInterval > 1 : true) &&
                // state.dessSocDifferent !== null &&, not used for now
                restrictionsValid
            );
        },
        [DYNAMIC_ESS.BUY_VALID]: state => {
            const {
                buyPriceFormula,
                fixedBuyPrices,
                buyDifferentPricesWeek,
                buyRowsWeek,
                buyRowsWeekend,
                buyErrorRowsWeek,
                buyErrorRowsWeekend,
                buyErrorPriceFormula,
                country,
                biddingZone,
                buyProvider,
                buyProviderName,
                buyPriceSamplingRate,
                apiKey,
                accountNumber,
                tempoOverwriteBlueDayHours,
                tempoOverwriteBlueNightHours,
                tempoOverwriteRedDayHours,
                tempoOverwriteRedNightHours,
                tempoOverwriteWhiteDayHours,
                tempoOverwriteWhiteNightHours,
            } = state;
            const validTempoOverwriteBlueDayHours = tempoOverwriteBlueDayHours
                ? validateInputNr(tempoOverwriteBlueDayHours)
                : true;
            const validTempoOverwriteBlueNightHours = tempoOverwriteBlueNightHours
                ? validateInputNr(tempoOverwriteBlueNightHours)
                : true;
            const validTempoOverwriteRedDayHours = tempoOverwriteRedDayHours
                ? validateInputNr(tempoOverwriteRedDayHours)
                : true;
            const validTempoOverwriteRedNightHours = tempoOverwriteRedNightHours
                ? validateInputNr(tempoOverwriteRedNightHours)
                : true;
            const validTempoOverwriteWhiteDayHours = tempoOverwriteWhiteDayHours
                ? validateInputNr(tempoOverwriteWhiteDayHours)
                : true;
            const validTempoOverwriteWhiteNightHours = tempoOverwriteWhiteNightHours
                ? validateInputNr(tempoOverwriteWhiteNightHours)
                : true;

            const fixedValid =
                fixedBuyPrices &&
                (buyDifferentPricesWeek
                    ? buyRowsWeek.length !== 0 &&
                      buyRowsWeekend.length !== 0 &&
                      !buyErrorRowsWeek &&
                      !buyErrorRowsWeekend
                    : buyRowsWeek.length !== 0 && !buyErrorRowsWeek);

            const ukOctopusDynamicValid =
                country &&
                country.id == idCountryUnitedKingdom &&
                buyProvider &&
                buyProvider.idProvider == idProviderOctopus &&
                accountNumber &&
                accountNumber.length !== null &&
                apiKey &&
                apiKey.length !== null;

            const dynamicValid =
                !fixedBuyPrices &&
                buyPriceFormula &&
                buyPriceFormula.length !== 0 &&
                country &&
                country.id != idCountryUnitedKingdom &&
                biddingZone &&
                buyProvider &&
                buyProviderName &&
                !buyErrorPriceFormula &&
                validTempoOverwriteBlueDayHours &&
                validTempoOverwriteBlueNightHours &&
                validTempoOverwriteRedDayHours &&
                validTempoOverwriteRedNightHours &&
                validTempoOverwriteWhiteDayHours &&
                validTempoOverwriteWhiteNightHours &&
                PRICE_SAMPLING_RATES.some(option => option.value === buyPriceSamplingRate);

            return fixedValid || dynamicValid || ukOctopusDynamicValid;
        },
        [DYNAMIC_ESS.SELL_VALID]: state => {
            const {
                sellPriceFormula,
                fixedSellPrices,
                sellDifferentPricesWeek,
                sellRowsWeek,
                sellRowsWeekend,
                sellErrorRowsWeek,
                sellErrorRowsWeekend,
                sellProvider,
                sellProviderName,
                sellPriceSamplingRate,
                sellErrorPriceFormula,
                country,
                apiKey,
                accountNumber,
            } = state;
            const fixedValid =
                fixedSellPrices &&
                (sellDifferentPricesWeek
                    ? sellRowsWeek.length !== 0 &&
                      sellRowsWeekend.length !== 0 &&
                      !sellErrorRowsWeek &&
                      !sellErrorRowsWeekend
                    : sellRowsWeek.length !== 0 && !sellErrorRowsWeek);

            const ukOctopusDynamicValid =
                country &&
                +country.id === idCountryUnitedKingdom &&
                sellProvider &&
                +sellProvider.idProvider === idProviderOctopus &&
                accountNumber &&
                accountNumber.length !== null &&
                apiKey &&
                apiKey.length !== null;

            const dynamicValid =
                !fixedSellPrices &&
                sellPriceFormula &&
                sellPriceFormula.length !== 0 &&
                country &&
                +country.id !== idCountryUnitedKingdom &&
                sellProvider &&
                sellProviderName &&
                !sellErrorPriceFormula &&
                PRICE_SAMPLING_RATES.some(option => option.value === sellPriceSamplingRate);

            return fixedValid || dynamicValid || ukOctopusDynamicValid;
        },
        [DYNAMIC_ESS.FORM_VALID]: (state, getters) => {
            const validators = [
                DYNAMIC_ESS.GRID_VALID,
                DYNAMIC_ESS.BATTERY_VALID,
                DYNAMIC_ESS.BUY_VALID,
                state.energyGridSell && DYNAMIC_ESS.SELL_VALID,
            ].filter(Boolean);
            return validators.every(validator => getters[validator]);
        },

        [DYNAMIC_ESS.BIDDING_ZONES]: state => {
            if (!state.country || !state.biddingZonesByCountry) {
                return [];
            }

            return state.biddingZonesByCountry[state.country.id];
        },

        [DYNAMIC_ESS.PROVIDERS]: state => {
            if (!state.country || !state.providersByCountry) {
                return [];
            }

            return state.providersByCountry[state.country.id] || [];
        },

        [DYNAMIC_ESS.CAPABILITIES]: state => {
            return state.capabilities || [];
        },
        [DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_TYPE]: state => {
            if (state.batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.NO_EXPORT) {
                return 'discharge';
            } else if (state.batteryFlowRestriction === BATTERY_FLOW_RESTRICTIONS.NO_IMPORT) {
                return 'charge';
            }
            return '';
        },
        [DYNAMIC_ESS.BATTERY_FLOW_RESTRICTION_SUPPORTED]: state => {
            return state.capabilities.includes(DESS_CAPABILITIES.batteryFlowRestrictionsSupported);
        },
    },
    mutations: {
        [DYNAMIC_ESS.SET_FORM_FROM_API]: (state, data) => {
            applyData(state, data);
        },
        [DYNAMIC_ESS.SET_UP_PROVIDERS_MAPPING]: state => {
            const {
                buyProviderId,
                buyProviderName,
                sellProviderName,
                sellProviderId,
                providers,
            } = state;
            if (!providers) {
                return;
            }

            if (buyProviderId || buyProviderName) {
                const buyProvider = providers.find(p => p.idProvider == buyProviderId);
                if (buyProvider) {
                    state.buyProviderName = buyProvider.name;
                }
                state.buyProvider = buyProvider || {
                    id: -1,
                    name: i18n.t('dynamicess.notInTheList'),
                };
            }

            if (sellProviderId || sellProviderName) {
                const sellProvider = providers.find(p => p.idProvider == sellProviderId);
                if (sellProvider) {
                    state.sellProviderName = sellProvider.name;
                }
                state.sellProvider = sellProvider || {
                    id: -1,
                    name: i18n.t('dynamicess.notInTheList'),
                };
            }
        },
        [DYNAMIC_ESS.SET_UP_COUNTRY_BASED_ON_BIDDING]: state => {
            const { idBiddingZone, biddingZonesByCountry, countries, biddingZones } = state;

            if (!biddingZones || !idBiddingZone || !biddingZonesByCountry || !countries) {
                return;
            }
            const biddingZone = biddingZones.find(z => z.idZone == idBiddingZone);
            state.biddingZone = biddingZone;
            const country = countries.find(c => c.id === parseInt(biddingZone.idCountry));
            if (!country) {
                console.warn("Couldn't find country for bidding zone");
                return;
            }
            state.country = country;
        },
        [DYNAMIC_ESS.FILL_ZONES]: (state, zones) => {
            const countries = {};
            const biddingZonesByCountry = zones.reduce((acc, zone) => {
                countries[zone.idCountry] = zone.countryName;
                if (zone.idCountry in acc) {
                    acc[zone.idCountry].push(zone);
                } else {
                    acc[zone.idCountry] = [zone];
                }
                return acc;
            }, {});

            state.countries = Object.keys(countries).map(key => ({
                id: +key,
                name: countries[key],
            }));

            // Sort countries by name
            state.countries.sort((a, b) => a.name.localeCompare(b.name));

            state.biddingZones = zones;
            state.biddingZonesByCountry = biddingZonesByCountry;
        },
        [DYNAMIC_ESS.FILL_PROVIDERS]: (state, providers) => {
            const providersByCountry = providers.reduce((acc, provider) => {
                if (provider.idCountry in acc) {
                    acc[provider.idCountry].push(provider);
                } else {
                    acc[provider.idCountry] = [provider];
                }
                return acc;
            }, {});
            state.providers = providers;
            state.providersByCountry = providersByCountry;
        },
        [DYNAMIC_ESS.SET_FORM_FIELD]: (state, { key, value }) => {
            if (!DYNAMIC_ESS_FORM_FIELDS[key]) {
                console.warn(`Unknown form field ${key}`);
                return;
            }

            window.dataLayer.push({
                event: 'gtag.dess_set_form_field',
                field: key,
                value: value,
            });
            if (key === DYNAMIC_ESS_FORM_FIELDS.country) {
                const biddingZones = state.biddingZonesByCountry[value.id];

                if (biddingZones.length === 1) {
                    state.biddingZone = biddingZones[0];
                } else {
                    // clear bidding zone and provider
                    state.biddingZone = null;
                    state.provider = null;
                }

                const providers = state.providersByCountry[value.id] || [];
                if (providers.length === 1) {
                    // Only a single provider exists. Select that by default.
                    if (
                        !vrmConfig.EdfTempoEnabled &&
                        parseInt(providers[0].idProvider) == ID_PROVIDER_EDF_TEMPO
                    ) {
                        state.buyProvider = null;
                        state.buyProviderName = '';
                    } else {
                        state.buyProvider = providers[0];
                        state.buyProviderName = state.buyProvider.name;
                    }
                    if (parseInt(providers[0].idProvider) !== ID_PROVIDER_EDF_TEMPO) {
                        // Avoid storing EDF Tempo as sell provider
                        state.sellProvider = providers[0];
                        state.sellProviderName = state.sellProvider.name;
                    } else {
                        state.sellProvider = null;
                        state.sellProviderName = '';
                    }
                } else {
                    // Clear buy and sell provider info
                    state.buyProvider = null;
                    state.buyProviderName = '';
                    state.sellProvider = null;
                    state.sellProviderName = '';
                }
            }

            if (key === DYNAMIC_ESS_FORM_FIELDS.buyProviderName) {
                if (state.sellProvider.id === -1) {
                    state.sellProviderName = value;
                }
            }

            if (key === DYNAMIC_ESS_FORM_FIELDS.buyProvider) {
                if (value && value.id !== -1) {
                    state.buyProviderName = value.name;
                } else if (value.id === -1) {
                    state.buyProviderName = '';
                }

                if (!state.sellProvider) {
                    state.sellProviderName = state.buyProviderName;
                    state.sellProvider = value;
                    if (value.sellPriceFormula) {
                        state.sellPriceFormula = value.sellPriceFormula;
                    } else {
                        state.sellPriceFormula = 'p';
                    }
                }

                if (value.buyPriceFormula) {
                    state.buyPriceFormula = value.buyPriceFormula;
                } else {
                    state.buyPriceFormula = 'p';
                }
            }

            if (key === DYNAMIC_ESS_FORM_FIELDS.sellProvider) {
                if (value && value.id !== -1) {
                    state.sellProviderName = value.name;
                } else if (value.id === -1) {
                    state.sellProviderName = '';
                }

                if (value.sellPriceFormula) {
                    state.sellPriceFormula = value.sellPriceFormula;
                } else {
                    state.sellPriceFormula = 'p';
                }
            }

            state[key] = value;
        },
        [DYNAMIC_ESS.SET_CAPABILITIES]: (state, installation) => {
            if (!installation || !installation.data || !installation.data.decp) {
                state.capabilities = [];
                return;
            }
            const rawValue = installation.data.decp.rawValue;
            const capabilities = Object.values(DESS_CAPABILITIES).filter(
                valueEnum => rawValue && !!(rawValue & valueEnum),
            );
            state.capabilities = capabilities;
        },
    },
};
